import { Button, Label, TargetIcon, Typography } from 'procyon-ui'
import React, { useState } from 'react'
import { useChannelBrowser } from '../../../providers/hooks'
import useMultiSlice from 'Core/Hooks/useMultiSlice'
import { SelectChannel } from '../../Modal'
import { enqueueNotification } from 'Utils/Helpers'
import _ from 'lodash'
import { createRsrcKey } from 'features/resources'
import { getFriendlyName } from 'Utils/FriendlyName'
import { reduxApiClient } from 'infra'
import { LoadingFeedback } from 'V2Components'
import { getHTMLDataTestID } from 'infra/utils'

const MsTeamNotificationWebhook = () => {
  const { linkedChannels, refresh, integration } = useChannelBrowser()
  const { slices, getObjectRef } = useMultiSlice(['notificationSubscriptions'])
  const [showModal, setShowModal] = useState(false)
  const [loading, setLoading] = useState(false)
  const [notificationType, setNotificationType] = useState('')
  const [isWebhookChannelSelected, setIsWebhookChannelSelected] = useState(false)
  const [selectedChannel, setSelectedChannel] = useState(null)

  const hasWebhookUrl = () => integration?.Spec?.WebhookUrl !== ''

  // Gateway Channel Helpers
  const getGatewayChannel = () => {
    const channelRefId =
      integration?.MsTeamsIntegrationSettingsMap?.MsTeamsIntegrationSettingsMap
        ?.medusaNodeNotificationChannelRef?.RefID

    if (!channelRefId) return false
    if (hasWebhookUrl() && channelRefId) return true

    const channel = linkedChannels.find((ch) => ch.ObjectMeta.ID === channelRefId)
    return channel || false
  }

  // Private Channel Helpers
  const getPrivateNotificationSubscription = () => {
    return slices.notificationSubscriptions.find((subscription) => {
      const { SubscriptionFor, Subscriber } = subscription.Spec
      return (
        SubscriptionFor.RefID === '0' &&
        SubscriptionFor.RefKind === 'Server' &&
        Subscriber.RefKind === 'MsTeamsChannel'
      )
    })
  }

  const getWebhookPrivateChannel = () => {
    return slices.notificationSubscriptions.find((subscription) => {
      const { Subscriber } = subscription.Spec
      return (
        hasWebhookUrl() &&
        Subscriber.RefKind === 'MsTeamsIntegration' &&
        Subscriber.RefID === integration?.ObjectMeta?.ID
      )
    })
  }

  const getPrivateServerChannel = () => {
    const privateSubscription = getPrivateNotificationSubscription()
    if (privateSubscription) {
      return getObjectRef(privateSubscription.Spec.Subscriber)
    }
    return null
  }

  // UI Helper Functions - Gateway
  const gatewayChannel = getGatewayChannel()

  const renderGatewayIcon = () => {
    const hasChannel = gatewayChannel && Object.keys(gatewayChannel).length > 1
    return hasChannel || hasWebhookUrl() ? <TargetIcon type='TEAMS' /> : ''
  }

  const getGatewayVariant = () => {
    return gatewayChannel && Object.keys(gatewayChannel).length > 1 ? 'grayBlue' : 'gray'
  }

  const renderGatewayChannelName = () => {
    if (gatewayChannel && !hasWebhookUrl()) {
      return <strong>{gatewayChannel?.Spec?.ChannelName}</strong>
    }
    if (gatewayChannel && hasWebhookUrl()) {
      return <strong>Webhook Channel</strong>
    }
    return <em>No channel connected yet</em>
  }

  // UI Helper Functions - Private
  const privateServerChannel = getPrivateServerChannel()

  const renderPrivateIcon = () => {
    const hasChannel = privateServerChannel && Object.keys(privateServerChannel).length > 1
    return hasChannel || hasWebhookUrl() ? <TargetIcon type='TEAMS' /> : ''
  }

  const getPrivateVariant = () => {
    return privateServerChannel && Object.keys(privateServerChannel).length > 1
      ? 'grayBlue'
      : 'gray'
  }

  const renderPrivateChannelName = () => {
    if (getWebhookPrivateChannel() && hasWebhookUrl()) {
      return <strong>Webhook Channel</strong>
    }
    if (privateServerChannel) {
      return <strong>{privateServerChannel?.Spec?.ChannelName}</strong>
    }
    return <em>No channel connected yet</em>
  }

  // Modal Handlers
  const handleModalClick = (type) => {
    setNotificationType(type)

    // Set initial webhook selection state based on current configuration
    if (type === 'getway') {
      setIsWebhookChannelSelected(hasWebhookUrl() && gatewayChannel)
    } else if (type === 'private') {
      setIsWebhookChannelSelected(hasWebhookUrl() && getWebhookPrivateChannel())
    }

    setShowModal(true)
  }

  const handleChannelSelection = (newValue) => {
    setIsWebhookChannelSelected(newValue)
  }

  // API Operations
  const updateGatewayChannel = async () => {
    try {
      setLoading(true)
      const payload = _.cloneDeep(integration)
      payload.MsTeamsIntegrationSettingsMap.MsTeamsIntegrationSettingsMap.medusaNodeNotificationChannelRef =
        {
          RefID: integration?.ObjectMeta?.ID,
          RefKind: 'MsTeamsIntegration'
        }

      await reduxApiClient('msteamsintegrations').update(payload)
      enqueueNotification('Gateway Notification channel updated successfully.', 'info')
    } catch (error) {
      enqueueNotification('Operation Failed', 'error')
    } finally {
      setLoading(false)
      setShowModal(false)
    }
  }

  const updatePrivateChannel = async (selectedChannels = {}) => {
    try {
      setLoading(true)

      // Handle deselection
      if (!Object.keys(selectedChannels).length && !hasWebhookUrl()) {
        const privateSubscription = getPrivateNotificationSubscription()
        if (privateSubscription) {
          await reduxApiClient('notificationsubscriptions').delete(privateSubscription)
          refresh()
          setShowModal(false)
        }
        return
      }

      const selectedChannelRef = {
        RefKind: 'MsTeamsIntegration',
        RefID: integration?.ObjectMeta?.ID
      }

      const channelKey = createRsrcKey(selectedChannelRef)
      const existingSubscription = getPrivateNotificationSubscription()

      // Avoid duplicate operation
      if (channelKey === createRsrcKey(privateServerChannel)) {
        setLoading(false)
        return
      }

      // Delete existing subscription if present
      if (existingSubscription) {
        await reduxApiClient('notificationsubscriptions').delete(existingSubscription)
      }

      // Create new subscription
      const newSubscription = {
        ObjectMeta: {
          Name: `PRIVATE_${getFriendlyName()}`
        },
        Spec: {
          SubscriptionFor: {
            RefID: '0',
            RefKind: 'Server'
          },
          Subscriber: selectedChannelRef
        }
      }

      await reduxApiClient('notificationsubscriptions').create(newSubscription)
      enqueueNotification('Private Server Notification Channel changed successfully!', 'info')
      refresh()
    } catch (error) {
      enqueueNotification('Failed to change Notification Channel!', 'error')
      refresh()
    } finally {
      setShowModal(false)
      setLoading(false)
    }
  }

  const onConfirmChannelChange = (channelData, type) => {
    // Check if webhook is selected
    if (channelData?.isWebhook || isWebhookChannelSelected) {
      if (type === 'getway') {
        updateGatewayChannel()
      } else {
        updatePrivateChannel({})
      }
    } else {
      if (type === 'getway') {
        updateGatewayChannel()
      } else {
        updatePrivateChannel(channelData)
      }
    }
  }

  return (
    <div className='mt-8'>
      {/* Gateway Notification Channel */}
      <div className='border-b-2'>
        <Typography variant='h4-medium'>GateWay Notification Channel</Typography>
        <div className='flex justify-between items-center my-5'>
          <Label
            size='lg'
            icon={renderGatewayIcon()}
            text={renderGatewayChannelName()}
            variant={getGatewayVariant()}
            style={{ padding: '15px' }}
          />
          <Button
            data-testid={getHTMLDataTestID('integration', 'button', 'select-getway')}
            size='md'
            variant='gray'
            onClick={() => handleModalClick('getway')}
          >
            Select
          </Button>
        </div>
      </div>

      {/* Private Server Notification Channel */}
      <div className='border-b-2 mt-10'>
        <Typography variant='h4-medium'>Private Server Notification Channel</Typography>
        <div className='flex justify-between items-center my-5'>
          <Label
            size='lg'
            icon={renderPrivateIcon()}
            text={renderPrivateChannelName()}
            variant={getPrivateVariant()}
            style={{ padding: '15px' }}
          />
          <Button
            data-testid={getHTMLDataTestID('integration', 'button', 'select-private')}
            size='md'
            variant='gray'
            onClick={() => handleModalClick('private')}
          >
            Select
          </Button>
        </div>
      </div>

      <SelectChannel
        showModal={showModal}
        activeChannel={selectedChannel}
        setShowModal={setShowModal}
        onConfirmChannelChange={onConfirmChannelChange}
        notificationType={notificationType}
        handleChannelSelection={handleChannelSelection}
        isWebhookChannelSelected={isWebhookChannelSelected}
      />

      <LoadingFeedback
        loading={loading}
        message='Updating MS Teams Notification Channel'
        caption='Please wait..'
      />
    </div>
  )
}

export { MsTeamNotificationWebhook }
